import moment from 'moment';
import { cloneDeep, reduce, isEmpty } from 'lodash';

import { formatNumber } from 'utils/number-formats';
import { isTableType } from 'constants/chart-roles';

/**
 * Each element should have two fields, shouldApply and fmt.
 *
 * shouldApply takes a channel and its customisations and should return a truthy value if the customisation is applicable
 * fmt is a function of the form (value, customisation) => { raw: value, display: <Customisation specific formatting string> }
 */
const FORMATS = [
  {
    // unbinned number formatting
    shouldApply: (channel, customisations) =>
      !channel.isBinning && customisations.numberFormatting,
    fmt: formatNumber,
  },
  {
    // unbinned date time formatting
    shouldApply: (channel, customisations) =>
      !channel.isBinning && customisations.dateTimeFormatting,
    fmt: (value, customisations) =>
      moment.utc(value).format(customisations.dateTimeFormatting.value),
  },
];

// Find a formatting function given a customisation
function applicableFormat(channel, customisation) {
  if (!(channel && customisation)) {
    return;
  }
  const channelFormat = FORMATS.find(format =>
    format.shouldApply(channel, customisation)
  );
  return channelFormat ? channelFormat.fmt : undefined;
}

/**
 * This stage adds formatting functions for use in the TableItemCellRenderer. This is because manually altering the documents is slower than doing the
 * alterations virtually.
 * @param {Object} stageData - Object containing a spec and an environment with customisations, chartType and channels
 * @returns {Object} - Object with an updated table spec with arguments for the TableItemCellRenderer
 */
function tableElementFormatting(stageData) {
  const {
    environment: { customisations, chartType, channels },
    spec,
  } = stageData;

  const formatCustomisations = reduce(
    channels,
    (acc, channel, channelName) => {
      const channelCustomisation = customisations.channels[channelName];
      if (channelCustomisation) {
        const fmt = applicableFormat(channel, channelCustomisation);
        if (fmt) {
          acc[channelName] = fmt;
        }
      }
      return acc;
    },
    {}
  );

  if (!isTableType(chartType) || isEmpty(formatCustomisations)) {
    return {};
  }

  const specWithFormatting = {
    ...spec,
    columnDefs: spec.columnDefs.map(column => {
      const customisation = cloneDeep(customisations.channels[column.field]); // AgGrid attempts to mutate this object for state tracking
      const fmt = formatCustomisations[column.field];

      const cellRendererParams = {
        fmt,
        customisation,
      };
      return {
        ...column,
        ...(fmt ? { cellRendererParams } : {}),
      };
    }),
  };

  return {
    spec: specWithFormatting,
  };
}

export default {
  input: [
    'environment.customisations',
    'environment.chartType',
    'environment.channels',
    'spec',
  ],
  fn: tableElementFormatting,
};
export { tableElementFormatting };
